import { FC, useEffect, useRef, useState } from 'react';
import { alpha, Checkbox, styled } from '@mui/material';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { EllipsisTooltipTypography } from '@/components/_common/EllipsisTooltipTypography';

type NewScheduleAvailItemType = 'error' | 'success' | 'warning';
type NewScheduleAvailItemVariant = 'default' | 'removable';
export type NewScheduleAvailItemChangeType = 'unchanged' | 'added' | 'removed';

type NewScheduleAvailItemProps = {
  background?: string;
  disabled?: boolean;
  fadeAnimMs?: number;
  onClick?: () => void;
  primaryLabel: string;
  selectAnimMs?: number;
  type?: NewScheduleAvailItemType;
  variant?: NewScheduleAvailItemVariant;
  isSelectedItem: boolean;
  hasRights?: boolean;
  changeType?: NewScheduleAvailItemChangeType;
  showCheckbox?: boolean;
  disableTakedown?: boolean;
};

const NewScheduleAvailItem: FC<NewScheduleAvailItemProps> = ({
  disabled,
  fadeAnimMs = 100,
  onClick,
  primaryLabel,
  selectAnimMs = 100,
  variant = 'default',
  isSelectedItem,
  hasRights = false,
  changeType = 'unchanged',
  showCheckbox = false,
  disableTakedown = false
}) => {
  const showRemove = variant === 'removable';
  const isRemoved = changeType === 'removed';

  const [isSelectedVisual, setIsSelectedVisual] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  const timersRef = useRef<number[]>([]);

  useEffect(() => {
    return () => timersRef.current.forEach(clearTimeout);
  }, []);

  const handleClick = () => {
    if (disabled) return;

    if (!showRemove) {
      // show purple immediately
      setIsSelectedVisual(true);

      // then start fading after the "select" delay
      const t1 = window.setTimeout(() => {
        setIsFadingOut(true);

        const t2 = window.setTimeout(() => {
          onClick?.();
        }, fadeAnimMs);

        timersRef.current.push(t2);
      }, selectAnimMs);

      timersRef.current.push(t1);
      return;
    }

    onClick?.();
  };

  useEffect(() => {
    if (disabled) return;

    // when the item becomes "not selected" again (because parent state didn't select it),
    // reset visuals (only for default variant).
    if (!showRemove && !isSelectedItem) {
      setIsSelectedVisual(false);
      setIsFadingOut(false);
    }
  }, [disabled, isSelectedItem, showRemove]);

  if (isSelectedItem) return null;

  return (
    <StyledContainer $fadeMs={fadeAnimMs} $fadeOut={isFadingOut} onClick={handleClick} disabletakedown={disableTakedown}>
      <LanguageBar
        $isAnimating={isSelectedVisual}
        $hasRights={hasRights}
        $changeType={changeType}
        $disabled={!!disabled}
        disabletakedown={disableTakedown}
      >
        <LanguageMainContent>
          <TextWrapper>
            <EllipsisTooltipTypography>{primaryLabel}</EllipsisTooltipTypography>
          </TextWrapper>
          {showCheckbox && <StyledCheckbox checked={!isRemoved} indeterminate={isRemoved} disableRipple tabIndex={-1} $changeType={changeType} />}
        </LanguageMainContent>
      </LanguageBar>
    </StyledContainer>
  );
};

export default NewScheduleAvailItem;

const StyledContainer = styled('div', {
  shouldForwardProp: (prop) => !['$fadeMs', '$fadeOut', 'disabletakedown'].includes(String(prop))
})<{
  $fadeMs: number;
  $fadeOut: boolean;
  disabletakedown: boolean;
}>`
  width: 100%;
  min-height: 55px;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  cursor: ${({ disabletakedown }) => (disabletakedown ? 'default' : 'pointer')};
  opacity: ${({ $fadeOut }) => ($fadeOut ? 0 : 1)};
  transform: ${({ $fadeOut }) => ($fadeOut ? 'translateY(-4px)' : 'none')};

  transition:
    opacity ${({ $fadeMs }) => $fadeMs}ms ease,
    transform ${({ $fadeMs }) => $fadeMs}ms ease;
`;

const LanguageBar = styled('div', {
  shouldForwardProp: (prop) => !['$isAnimating', '$hasRights', '$changeType', '$disabled', 'disabletakedown'].includes(String(prop))
})<{
  $isAnimating: boolean;
  $hasRights: boolean;
  $changeType: NewScheduleAvailItemChangeType;
  $disabled: boolean;
  disabletakedown: boolean;
}>(({ theme, $isAnimating, $hasRights, $changeType, $disabled, disabletakedown }) => {
  const focusTheme = theme as FocusTheme;
  const isRemoved = $changeType === 'removed';

  const defaultBorder = $isAnimating
    ? `1px solid ${theme.palette?.tertiary?.light ?? theme.palette.primary.light}`
    : $hasRights
      ? `1px solid ${theme.palette.primary.main}`
      : `1px dashed ${focusTheme.palette.neutral[30]}`;

  return {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    cursor: $disabled || (disabletakedown && $changeType === 'unchanged') ? 'default' : 'pointer',
    padding: theme.spacing(2),
    margin: theme.spacing(0, 0, 0.5),
    borderRadius: 6,
    border: isRemoved ? `1px solid ${theme.palette.error.light}` : defaultBorder,
    backgroundColor: $isAnimating ? '#231D35' : focusTheme.palette.neutral[80],
    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',

    ...(!(disabletakedown && $changeType === 'unchanged') && {
      '&:hover': {
        background: $disabled
          ? focusTheme.palette.neutral[80]
          : $isAnimating
            ? '#231D35'
            : $hasRights
              ? alpha(theme.palette.primary.main, 0.5)
              : focusTheme.palette.neutral[60]
      }
    })
  };
});

const LanguageMainContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  gap: theme.spacing(2)
}));

const TextWrapper = styled('div')({
  flex: '1 1 0',
  minWidth: 0,
  overflow: 'hidden'
});

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== '$changeType'
})<{
  $changeType: NewScheduleAvailItemChangeType;
}>(({ theme, $changeType }) => {
  const focusTheme = theme as FocusTheme;
  const isAdded = $changeType === 'added';
  const isRemoved = $changeType === 'removed';

  const baseColor = isRemoved ? theme.palette.error.main : isAdded ? theme.palette.primary.main : focusTheme.palette.neutral[40];

  return {
    flexShrink: 0,
    padding: 0,
    marginLeft: theme.spacing(1),
    color: baseColor,
    '&.Mui-checked': {
      color: baseColor
    },
    '&.MuiCheckbox-indeterminate': {
      color: theme.palette.error.main
    },
    '&.Mui-disabled': {
      color: focusTheme.palette.neutral[50]
    }
  };
});
